import React, { useState } from 'react';
import {
  Brain,
  FileText,
  Trash2,
  GripVertical,
  Plus,
  Sparkles,
  Loader2,
  ChevronDown,
  ChevronUp,
  RefreshCcw,
} from 'lucide-react';
import { makeId } from '../CreateInterviewModal';
import { aiApi } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const MAX_SKILLS = 5;
const QUESTIONS_PER_GENERATION = 5;

function GeneratedQuestionsList({ questions, onUpdate, onRemove, onClear }) {
  if (!questions || questions.length === 0) return null;
  return (
    <div className="mt-3 rounded-xl border border-[#E9E3FB] bg-[#FAF8FF] p-3">
      <div className="flex items-center justify-between">
        <span className="text-[12.5px] font-semibold text-[#1A1533] inline-flex items-center gap-1.5">
          <Sparkles size={13} className="text-[#5B4FE8]" />
          AI-generated questions ({questions.length})
        </span>
        <button
          onClick={onClear}
          className="text-[11.5px] text-[#8A83A5] hover:text-[#DC3545] inline-flex items-center gap-1"
        >
          <Trash2 size={11} /> Clear all
        </button>
      </div>
      <ol className="mt-2 space-y-2">
        {questions.map((q, i) => (
          <li
            key={`q-${i}`}
            className="rounded-lg bg-white border border-[#ECE6FA] p-3"
            data-testid={`skill-question-${i}`}
          >
            <div className="flex items-start gap-2">
              <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full bg-[#F1ECFE] text-[#5B4FE8] text-[11px] font-bold inline-flex items-center justify-center">
                {i + 1}
              </span>
              <div className="flex-1 min-w-0 space-y-1.5">
                <textarea
                  value={q.question}
                  onChange={(e) => onUpdate(i, { question: e.target.value })}
                  rows={2}
                  data-testid={`skill-question-input-${i}`}
                  className="w-full text-[13px] text-[#1A1533] leading-snug bg-transparent border border-transparent hover:border-[#ECE6FA] focus:border-[#5B4FE8] focus:bg-white rounded-md px-2 py-1.5 outline-none resize-none transition-colors"
                />
                <div className="rounded-md bg-[#FAF8FF] border border-transparent hover:border-[#ECE6FA] focus-within:border-[#5B4FE8] focus-within:bg-white transition-colors">
                  <span className="block text-[11px] font-semibold text-[#1A1533] px-2 pt-1.5">Ideal answer</span>
                  <textarea
                    value={q.ideal_answer || ''}
                    onChange={(e) => onUpdate(i, { ideal_answer: e.target.value })}
                    rows={2}
                    data-testid={`skill-ideal-input-${i}`}
                    placeholder="Add an ideal answer (optional)"
                    className="w-full text-[12px] text-[#4A4463] leading-snug bg-transparent px-2 pb-1.5 outline-none resize-none placeholder-[#9A93B0]"
                  />
                </div>
              </div>
              <button
                onClick={() => onRemove(i)}
                className="shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-[#DC3545] hover:bg-[#FDECEC]"
                aria-label="Remove question"
              >
                <Trash2 size={12} />
              </button>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function SkillRow({ skill, index, onUpdate, onRemove }) {
  const { toast } = useToast();
  const [generating, setGenerating] = useState(false);
  const [open, setOpen] = useState(true);
  const questions = skill.questions || [];
  const hasName = skill.name.trim().length > 0;

  const generate = async (mode = 'append') => {
    if (!hasName || generating) return;
    setGenerating(true);
    try {
      const res = await aiApi.skillQuestions({
        skill: skill.name.trim(),
        description: skill.desc || '',
        count: QUESTIONS_PER_GENERATION,
      });
      const items = Array.isArray(res?.questions) ? res.questions : [];
      if (items.length === 0) {
        toast({
          title: 'No questions generated',
          description: 'AI returned an empty list. Try refining the description and try again.',
        });
        return;
      }
      const next =
        mode === 'replace' ? items : [...(skill.questions || []), ...items];
      onUpdate({ questions: next });
      setOpen(true);
      toast({
        title: 'Questions generated',
        description: `${items.length} new question${items.length === 1 ? '' : 's'} added for "${skill.name.trim()}".`,
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'AI service is unavailable.';
      toast({ title: 'Generation failed', description: msg });
    } finally {
      setGenerating(false);
    }
  };

  const removeQuestion = (idx) => {
    onUpdate({ questions: questions.filter((_, i) => i !== idx) });
  };
  const updateQuestion = (idx, patch) => {
    onUpdate({ questions: questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)) });
  };
  const clearQuestions = () => onUpdate({ questions: [] });

  return (
    <div className="flex items-stretch gap-2">
      <div className="flex items-center px-1 text-[#8A83A5]">
        <GripVertical size={14} />
      </div>
      <div className="flex-1 space-y-2">
        <div className="relative">
          <Brain size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A83A5]" />
          <input
            value={skill.name}
            onChange={(e) => onUpdate({ name: e.target.value })}
            placeholder={`Enter skill #${index + 1}`}
            className="w-full pl-9 pr-10 py-2.5 rounded-xl border border-[#ECE6FA] bg-[#FAF8FF] outline-none text-[13.5px] placeholder-[#9A93B0] focus:ring-2 focus:ring-[#5B4FE8]/30"
          />
          <button
            onClick={onRemove}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md flex items-center justify-center text-[#DC3545] hover:bg-[#FDECEC]"
            aria-label="Remove skill"
          >
            <Trash2 size={14} />
          </button>
        </div>
        <div className="relative">
          <FileText size={15} className="absolute left-3 top-3 text-[#8A83A5]" />
          <textarea
            value={skill.desc}
            onChange={(e) => onUpdate({ desc: e.target.value })}
            placeholder="Feel free to describe the skill in 1-2 sentences to further tailor the questions (optional)"
            rows={2}
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#ECE6FA] bg-[#FAF8FF] outline-none text-[13px] placeholder-[#9A93B0] focus:ring-2 focus:ring-[#5B4FE8]/30 resize-none"
          />
        </div>

        {/* AI generate controls */}
        <div className="flex items-center flex-wrap gap-2 pt-1">
          <button
            onClick={() => generate('append')}
            disabled={!hasName || generating}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#5B4FE8] to-[#7A6BFF] text-white text-[12.5px] font-semibold shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            title={hasName ? `Generate ${QUESTIONS_PER_GENERATION} questions with AI` : 'Enter a skill name first'}
          >
            {generating ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Sparkles size={12} />
            )}
            {generating ? 'Generating…' : `Generate with AI`}
          </button>

          {questions.length > 0 && (
            <>
              <button
                onClick={() => generate('replace')}
                disabled={!hasName || generating}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F1ECFE] text-[#5B4FE8] text-[12.5px] font-semibold hover:bg-[#E9E3FB] disabled:opacity-50"
              >
                <RefreshCcw size={11} /> Regenerate
              </button>
              <button
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#4A4463] hover:text-[#1A1533]"
              >
                {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {open ? 'Hide' : 'Show'} {questions.length} question{questions.length === 1 ? '' : 's'}
              </button>
            </>
          )}

          {!hasName && (
            <span className="text-[11.5px] text-[#8A83A5]">
              Add a skill name to enable AI generation
            </span>
          )}
        </div>

        {open && (
          <GeneratedQuestionsList
            questions={questions}
            onUpdate={updateQuestion}
            onRemove={removeQuestion}
            onClear={clearQuestions}
          />
        )}
      </div>
    </div>
  );
}

export default function SkillConversationsPanel({ module, onChange }) {
  const skills = module.skills || [];

  const updateSkill = (id, patch) => {
    onChange({ skills: skills.map((s) => (s.id === id ? { ...s, ...patch } : s)) });
  };

  const removeSkill = (id) => {
    onChange({ skills: skills.filter((s) => s.id !== id) });
  };

  const addSkill = () => {
    if (skills.length >= MAX_SKILLS) return;
    onChange({ skills: [...skills, { id: makeId('skill'), name: '', desc: '', questions: [] }] });
  };

  return (
    <div>
      <h3 className="text-[17px] font-bold text-[#1A1533]">Defined skills</h3>
      <p className="text-[13px] text-[#4A4463] mt-2 leading-relaxed">
        Define the skills you want to test candidates on. It can be any skill from sales negotiation to react.js and beyond. The optional description can be used to refine the questions or avoid certain areas within the skill.
      </p>
      <p className="text-[12.5px] text-[#5B4FE8] mt-2 inline-flex items-center gap-1.5">
        <Sparkles size={12} />
        Tip: name a skill, then click <span className="font-semibold">Generate with AI</span> to draft 5 interview questions instantly.
      </p>

      <div className="mt-5 space-y-4">
        {skills.map((s, idx) => (
          <SkillRow
            key={s.id}
            skill={s}
            index={idx}
            onUpdate={(patch) => updateSkill(s.id, patch)}
            onRemove={() => removeSkill(s.id)}
          />
        ))}
      </div>

      <button
        onClick={addSkill}
        disabled={skills.length >= MAX_SKILLS}
        className="mt-5 text-[13px] font-semibold text-[#5B4FE8] hover:underline disabled:text-[#B8B3D4] disabled:no-underline inline-flex items-center gap-1"
      >
        <Plus size={13} />
        Add another skill (up to {Math.max(0, MAX_SKILLS - skills.length)} more)
      </button>
    </div>
  );
}
